import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  animate,
  style,
  transition,
  trigger,
} from '@angular/animations';
import type { ModalVariant, ModalSize } from '../utils/types';
import { ConfirmService } from '../confirm/confirm.service';

/** Modals open right now, newest last; only the top one answers Esc. */
const openModals: ModalComponent[] = [];

const MODAL_SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-full',
};

const MODAL_HEADER_COLORS: Record<ModalVariant, string> = {
  default: 'text-gray-900',
  danger: 'text-red-600',
  success: 'text-emerald-600',
  warning: 'text-amber-600',
};

const MODAL_ICON: Record<ModalVariant, string> = {
  default: '',
  danger:
    '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clip-rule="evenodd" /></svg>',
  success:
    '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clip-rule="evenodd" /></svg>',
  warning:
    '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clip-rule="evenodd" /></svg>',
};

@Component({
  selector: 'ui-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('backdrop', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('150ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('100ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
    trigger('dialog', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95) translateY(-8px)' }),
        animate('150ms ease-out', style({ opacity: 1, transform: 'scale(1) translateY(0)' })),
      ]),
      transition(':leave', [
        animate('100ms ease-in', style({ opacity: 0, transform: 'scale(0.95) translateY(-8px)' })),
      ]),
    ]),
  ],
})
export class ModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() title?: string;
  @Input() subtitle?: string;
  @Input() description?: string;
  @Input() size: ModalSize = 'md';
  @Input() variant: ModalVariant = 'default';
  @Input() showCloseButton = true;
  @Input() closeOnBackdropClick = true;
  @Input() closeOnEsc = true;
  @Input() isLoading = false;
  /** When true, body uses a flex column so embedded forms can scroll internally. */
  @Input() tall = false;
  /** Asks "Discard changes?" before closing once a form inside has been edited. */
  @Input({ transform: booleanAttribute }) confirmDiscard = false;

  @Output() readonly closed = new EventEmitter<void>();

  private readonly document = inject(DOCUMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly confirm = inject(ConfirmService);

  readonly sizeClasses = MODAL_SIZE_CLASSES;
  readonly headerColors = MODAL_HEADER_COLORS;
  readonly variantIcons = MODAL_ICON;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      this.track(this.isOpen);
    }
  }

  ngOnDestroy(): void {
    this.track(false);
  }

  /** Also the way for a form inside to cancel, so its Cancel button asks too. */
  async close(): Promise<void> {
    if (this.isLoading) {
      return;
    }
    if (this.confirmDiscard && this.hasChanges()) {
      const discard = await this.confirm.ask({
        title: 'Discard changes?',
        message: 'What you have entered in this form will be lost.',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
      });
      if (!discard) {
        return;
      }
    }
    this.closed.emit();
  }

  onBackdropClick(): void {
    if (this.closeOnBackdropClick) {
      void this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.closeOnEsc && openModals.at(-1) === this) {
      void this.close();
    }
  }

  /** Reactive forms mark edited controls ng-dirty, wherever in the body they sit. */
  private hasChanges(): boolean {
    return !!this.host.nativeElement.querySelector('.ui-modal__body .ng-dirty');
  }

  private track(open: boolean): void {
    const index = openModals.indexOf(this);
    if (index !== -1) {
      openModals.splice(index, 1);
    }
    if (open) {
      openModals.push(this);
    }
    // A modal underneath may still be open, so keep the page locked until the last one closes.
    this.document.body.style.overflow = openModals.length ? 'hidden' : '';
  }
}
